import { Autocomplete, Box, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import { CheckCircle as CheckIcon, Cancel as CancelIcon, Visibility as ViewIcon } from '@mui/icons-material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useCommandAudits } from '../../api/command-audits-api'
import { useFunctionApps } from '../../api/function-apps-api'
import { CommandAudit, CommandAuditType } from '../../types/CommandAudit'
import { FunctionApp } from '../../types/FunctionApp'
import { format } from 'date-fns'
import { dataGridSx } from '../../styles/dataGridSx'

const DEBOUNCE_MS = 400

const isAuditType = (value: string | null): value is CommandAuditType =>
  value === 'Execution' || value === 'TimerToggle'

const isSuccess = (row: CommandAudit) =>
  row.type === 'TimerToggle'
    ? !!row.success
    : row.statusCode != null && row.statusCode >= 200 && row.statusCode < 300

const formatBody = (body: string | null) => {
  if (!body) return ''
  try {
    return JSON.stringify(JSON.parse(body), null, 2)
  } catch {
    return body
  }
}

const preSx = {
  m: 0,
  p: 1.5,
  borderRadius: 1,
  bgcolor: 'action.hover',
  fontSize: '0.8rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 300,
  overflow: 'auto',
} as const

const AuditPage = () => {
  const { t, i18n } = useTranslation()
  const [apps, isLoadingApps] = useFunctionApps()
  const [searchParams, setSearchParams] = useSearchParams()

  const typeParam = searchParams.get('type')
  const filters = {
    functionAppName: searchParams.get('functionAppName') ?? undefined,
    functionName: searchParams.get('functionName') ?? undefined,
    userName: searchParams.get('userName') ?? undefined,
    type: isAuditType(typeParam) ? typeParam : undefined,
    offset: Number(searchParams.get('offset')) || 0,
    limit: Number(searchParams.get('limit')) || 50,
  }

  const [localFunctionName, setLocalFunctionName] = useState(filters.functionName ?? '')
  const [localUserName, setLocalUserName] = useState(filters.userName ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setLocalFunctionName(filters.functionName ?? '')
    setLocalUserName(filters.userName ?? '')
  }, [filters.functionName, filters.userName])

  const setFilters = useCallback(
    (updater: (prev: typeof filters) => typeof filters) => {
      setSearchParams((prev) => {
        const prevType = prev.get('type')
        const currentFilters = {
          functionAppName: prev.get('functionAppName') ?? undefined,
          functionName: prev.get('functionName') ?? undefined,
          userName: prev.get('userName') ?? undefined,
          type: isAuditType(prevType) ? prevType : undefined,
          offset: Number(prev.get('offset')) || 0,
          limit: Number(prev.get('limit')) || 50,
        }
        const next = updater(currentFilters)
        const params = new URLSearchParams()
        if (next.functionAppName) params.set('functionAppName', next.functionAppName)
        if (next.functionName) params.set('functionName', next.functionName)
        if (next.userName) params.set('userName', next.userName)
        if (next.type) params.set('type', next.type)
        if (next.offset) params.set('offset', String(next.offset))
        if (next.limit !== 50) params.set('limit', String(next.limit))
        return params
      }, { replace: true })
    },
    [setSearchParams],
  )

  const setDebouncedFilter = useCallback(
    (key: 'functionName' | 'userName', value: string) => {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setFilters((prev) => ({ ...prev, [key]: value || undefined, offset: 0 }))
      }, DEBOUNCE_MS)
    },
    [setFilters],
  )

  const [audits, isFetching] = useCommandAudits(filters)
  const [viewAudit, setViewAudit] = useState<CommandAudit | null>(null)

  const selectedApp = apps.find((a) => a.name === filters.functionAppName) ?? null

  const columns: GridColDef<CommandAudit>[] = [
    {
      field: 'dateTime',
      headerName: t('command.audit.date', 'Date'),
      flex: 1,
      valueFormatter: (value: string) => {
        if (!value) return ''
        return format(new Date(value), 'yyyy-MM-dd HH:mm:ss')
      },
    },
    {
      field: 'userName',
      headerName: t('command.audit.user', 'User'),
      flex: 1,
    },
    {
      field: 'functionAppName',
      headerName: t('command.audit.functionApp', 'Function App'),
      flex: 1,
    },
    {
      field: 'functionName',
      headerName: t('command.audit.functionName', 'Function'),
      flex: 1,
      renderCell: ({ row }) => {
        const prefix = row.type === 'TimerToggle' ? row.action : row.method
        return prefix ? `${prefix} ${row.functionName}` : row.functionName
      },
    },
    {
      field: 'reason',
      headerName: t('command.audit.reason', 'Reason'),
      flex: 1.5,
    },
    {
      field: 'type',
      headerName: t('command.audit.type', 'Type'),
      width: 130,
      renderCell: ({ row }) =>
        row.type === 'TimerToggle'
          ? t('command.audit.typeTimer', 'Timer toggle')
          : t('command.audit.typeExecution', 'Execution'),
    },
    {
      field: 'outcome',
      headerName: t('command.audit.outcome', 'Outcome'),
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {isSuccess(row) ? (
            <CheckIcon color="success" fontSize="small" />
          ) : (
            <CancelIcon color="error" fontSize="small" />
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) =>
        row.type === 'Execution' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <IconButton size="small" onClick={() => setViewAudit(row)} aria-label={t('command.audit.view', 'View')}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null,
    },
  ]

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('command.audit.title', 'Audit')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Autocomplete
          options={apps}
          getOptionLabel={(option) => option.name}
          loading={isLoadingApps}
          value={selectedApp}
          onChange={(_e, value: FunctionApp | null) => {
            setLocalFunctionName('')
            setFilters((prev) => ({ ...prev, functionAppName: value?.name ?? undefined, functionName: undefined, offset: 0 }))
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label={t('command.audit.functionApp', 'Function App')} size="small" />}
          sx={{ minWidth: 250 }}
        />
        <TextField
          label={t('command.audit.functionName', 'Function')}
          size="small"
          value={localFunctionName}
          onChange={(e) => {
            setLocalFunctionName(e.target.value)
            setDebouncedFilter('functionName', e.target.value)
          }}
          sx={{ minWidth: 250 }}
        />
        <TextField
          label={t('command.audit.user', 'User')}
          size="small"
          value={localUserName}
          onChange={(e) => {
            setLocalUserName(e.target.value)
            setDebouncedFilter('userName', e.target.value)
          }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          label={t('command.audit.type', 'Type')}
          size="small"
          value={filters.type ?? ''}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              type: isAuditType(e.target.value) ? e.target.value : undefined,
              offset: 0,
            }))
          }
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('command.audit.typeAll', 'All')}</MenuItem>
          <MenuItem value="Execution">{t('command.audit.typeExecution', 'Execution')}</MenuItem>
          <MenuItem value="TimerToggle">{t('command.audit.typeTimer', 'Timer toggle')}</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
        <DataGrid
          sx={dataGridSx}
          rows={audits}
          columns={columns}
          loading={isFetching}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={{ page: filters.offset / filters.limit, pageSize: filters.limit }}
          onPaginationModelChange={(model) =>
            setFilters((prev) => ({
              ...prev,
              offset: model.page * model.pageSize,
              limit: model.pageSize,
            }))
          }
          paginationMode="server"
          rowCount={audits.length < filters.limit ? filters.offset + audits.length : -1}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          localeText={i18n.language === 'fr' ? { noRowsLabel: 'Aucun enregistrement' } : undefined}
        />
      </Box>

      <Dialog open={!!viewAudit} onClose={() => setViewAudit(null)} maxWidth="md" fullWidth>
        <DialogTitle>{viewAudit?.functionName}</DialogTitle>
        <DialogContent>
          {viewAudit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {[viewAudit.method, viewAudit.statusCode].filter(Boolean).join(' · ')}
              </Typography>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('command.audit.payload', 'Payload')}
                </Typography>
                <Box component="pre" sx={preSx}>
                  {formatBody(viewAudit.payload)}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('command.audit.response', 'Response')}
                </Typography>
                <Box component="pre" sx={preSx}>
                  {formatBody(viewAudit.responseBody)}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default AuditPage
